import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const RoleOption = ({ value, title, text }) => (
  <label className="border rounded-xl p-3 cursor-pointer hover:border-primary flex items-start gap-3">
    <RadioGroupItem value={value} data-testid={`profile-role-${value}`} />
    <div>
      <div className="font-medium">{title}</div>
      <div className="text-xs text-muted-foreground">{text}</div>
    </div>
  </label>
);

export default function CompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ role: user?.role === "buyer" ? "buyer" : "seller", phone: user?.phone || "", company: user?.company || "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", form);
      setUser(data);
      toast.success("Profile saved");
      navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 sm:px-6 py-8 sm:py-16" data-testid="complete-profile-page">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-3xl text-primary tracking-tight">One last step</CardTitle>
          <p className="text-sm text-muted-foreground">Tell us how you'll use Indian RE Trade. Your phone is shown to members on listings you post.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <RadioGroup value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <RoleOption value="seller" title="Seller" text="List projects, land, grid or power." />
              <RoleOption value="buyer" title="Buyer" text="Discover and inquire about opportunities." />
            </RadioGroup>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+91 ..." />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input data-testid="profile-company" value={form.company} onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))} />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary hover:bg-primary/90" data-testid="profile-submit">
              {saving ? "Saving…" : "Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
